module.exports = toLocationProperties

/** Convert dash seperated location id string to location object
 *
 * Shipment origin and destination location id strings use only
 * dashes to seperate key/value pairs. There are three ways location
 * ids can be encoded:
 * 1. colon seperated key/value pairs: 'zone:nc:state:kano:lga:yagba-west'
 * 2. dash seperated: 'zone-nc-state-kano-lga-yagba-west'
 * 3. as objects: {zone: 'nc', state: 'kano', lga: 'yagba-west'}
 * This function converts from the second representation to the third.
 * It should be noted though, that dash seperated id strings should
 * be avoided where possible, because they are more error prone
 * to parse.
 *
 *  * EDGE CASES HANDLED:
 *
 * 1. Location keywords in SDP names:
 *    Input:  'country-ng-state-fct-sdp-medrackhealth-pharmacy-zone-5'
 *    Output: {sdp: 'medrackhealth-pharmacy-zone-5'}
 *    NOT:    {sdp: 'medrackhealth-pharmacy', zone: '5'}
 *
 *    Once we encounter 'sdp', we collect all subsequent parts as the SDP value,
 *    even if they contain location keywords like 'zone', 'state', 'lga'.
 *
 * 2. SDP names followed by 'name' key:
 *    Input:  'country-ng-state-abuja-sdp-utako-name-utako-place-pharm'
 *    Output: {sdp: 'utako', name: 'utako-place-pharm'}
 *    NOT:    {sdp: 'utako-name-utako-place-pharm'}
 *    The 'name' key is special - it's allowed to appear after 'sdp' and should
 *    be treated as a new location property, not part of the SDP name.
 *
 * 3. Duplicate prevention:
 *    The check `!props[str]` prevents re-parsing a key that's already been set,
 *    protecting against edge cases where a location keyword might appear multiple times.
 *
 */
function toLocationProperties (id) {
  if (id === 'national') {
    return {
      national: 'national',
      id
    }
  }

  if (id === 'country') {
    return {
      country: 'country',
      id
    }
  }

  const LOCATION_KEYS = ['country', 'zone', 'state', 'lga', 'sdp', 'name']

  // Parse the hyphen seperated id and create a colon seperate id from it
  // while also collecting the key/value pairs as object properties.
  const props = {}
  const parts = id.split('-')
  let key = ''
  let value = ''
  for (const str of parts) {
    // We check that a property for that `key` doesn't already exists, to guard against
    // satellite stores that contain `zone` in their name
    // Check if this part is a location keyword AND should start a new key/value pair
    // Conditions for switching to a new key:
    // 1. `str` is a recognized location keyword
    // 2. We haven't already set a property for this keyword (prevents duplicates)
    // 3. EITHER:
    //    a) We're not currently parsing an SDP value (key !== 'sdp'), OR
    //    b) We ARE parsing an SDP value BUT we've hit the 'name' keyword
    //       (name is the only keyword allowed to follow sdp as a new key)
    // 4. We're not currently parsing a 'name' value — name is terminal, nothing
    //    follows it as a new key. Without this guard, a location keyword embedded
    //    in the name (e.g. 'zamfara-state-pmi') would be split incorrectly into
    //    a separate key/value pair ('name:zamfara' + 'state:pmi').
    if (LOCATION_KEYS.includes(str) && !props[str] && !(key === 'sdp' && str !== 'name') && !(key === 'name')) {
      if (value) {
        props[key] = value
        props.id = props.id != null ? `${props.id}:${key}:${value}` : `${key}:${value}`
      }
      key = str
      value = ''
    } else {
      value = value ? `${value}-${str}` : str
    }
  }
  if (value) {
    props[key] = value
    props.id = props.id != null ? `${props.id}:${key}:${value}` : `${key}:${value}`
  }
  return props
}
